import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../../../../../projects/my-app/src/environments/environment';
import { combineLatest, filter, Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { DashboardTitleService } from '../../shared-services/dashboard-title.service';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector.component';
import { ThemeService } from '../../shared-services/theme.service';
import { LayoutService } from '../../shared-services/layout.service';
import { SlideshowService } from '../../shared-services/slideshow.service';

@Component({
  selector: 'app-side-navbar',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatTooltipModule,
    RouterModule,
  ],
  templateUrl: './side-navbar.component.html',
  styleUrls: ['./side-navbar.component.scss'],
})
export class SideNavbarComponent implements OnInit, OnDestroy {
  /** Sidebar drag-resize bounds + persistence key. Min/max chosen so the
   *  menu icons + labels stay readable at min and don't crowd content at max. */
  private static readonly SIDEBAR_MIN_WIDTH = 200;
  private static readonly SIDEBAR_MAX_WIDTH = 480;
  private static readonly SIDEBAR_DEFAULT_WIDTH = 280;
  private static readonly SIDEBAR_WIDTH_STORAGE_KEY = 'dvm.sidebar.expandedWidth';

  private readonly subscriptions = new Subscription();
  private themeSelectorDialogRef: MatDialogRef<ThemeSelectorComponent> | null = null;

  isExpanded = true;
  showSubmenu: boolean = false;
  isShowing = false;
  showSubSubMenu: boolean = false;
  isCollapsed = true;
  isResizingSidebar = false;
  sidebarExpandedWidth = SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;

  isMobileLayout = false;
  isMobileDrawerOpen = false;

  username: string = 'user';
  logo!: string;
  companyText = environment.COMPANY_LOGO_TEXT;
  hasClientLogo = false;
  clientLogo = '';
  clientIcon = '';
  clientText = '';

  secondaryColor!: string;
  secondaryColor2!: string;
  secondaryLogo!: string;
  toolbarBackgroundImage!: string;
  sidenavBackgroundImage!: string;
  useAltSidenav: boolean = false;

  menuItems: any[] = [];
  expandedMenus: Set<string> = new Set();
  isChatRoute: boolean = false;

  dashboardTitle = '';
  dashboardTitlePosition: 'top' | 'below' = 'below';
  dashboardTitleFontSize = '';

  showThemeOptions = true;
  showThemeToggle = true;
  hasPendingThemePreview = false;
  isDarkTheme = false;

  slideshowActive = false;
  slideshowPlaying = false;
  slideshowIndex = 0;
  slideshowTotal = 0;
  slideshowCurrentName = '';
  slideshowProgress = 0;

  // Macro panel state
  isMacroPanelOpen = false;
  isLoadingMacros = false;
  isLoadingMacroContent = false;
  macros: string[] = [];
  macroErrorMessage: string | null = null;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
    private responsiveLayoutService: ResponsiveLayoutService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dashboardTitleService: DashboardTitleService,
    private themeService: ThemeService,
    private layoutService: LayoutService,
    private slideshowService: SlideshowService,
  ) {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          this.isChatRoute = event.urlAfterRedirects.includes('/chat');
          if (this.isMobileLayout) {
            this.isMobileDrawerOpen = false;
          }
        })
    );

    this.subscriptions.add(
      this.breakpointObserver
        .observe('(max-width: 991.98px)')
        .subscribe((state) => {
          this.isMobileLayout = state.matches;
          if (!this.isMobileLayout) {
            this.isMobileDrawerOpen = false;
          }
        })
    );
  }

  async ngOnInit(): Promise<void> {
    this.sidebarExpandedWidth = this.getStoredSidebarWidth();
    this.initLogosAndColors();
    this.initToolbarState();
    this.subscribeToSharedState();

    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();

      this.username =
        `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim() || 'user';
    }

    this.loadSidenavConfig();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.themeSelectorDialogRef?.close();
    this.themeSelectorDialogRef = null;
  }

  initLogosAndColors(): void {
    const env = environment as any;
    const useAlt = env.USE_ALT_SIDENAV === true || env.USE_ALT_SIDENAV === 'true';
    this.useAltSidenav = useAlt;
    this.clientLogo = String(env.CLIENT_LOGO_SRC || env.CLIENT_LOGO || '').trim();
    this.clientIcon = String(env.CLIENT_ICON || '').trim();
    this.clientText = String(env.CLIENT_TEXT || env.CLIENT_LOGO_TEXT || '').trim();
    this.hasClientLogo = !!(this.clientLogo || this.clientIcon);

    console.log('USE_ALT_SIDENAV:', env.USE_ALT_SIDENAV, 'useAlt:', useAlt);

    if (useAlt) {
      this.logo = env.ALT_COMPANY_LOGO_SRC;
      this.secondaryLogo = env.ALT_SECONDARY_LOGO_SRC;

      this.toolbarBackgroundImage =
        'linear-gradient(to right, rgb(216, 45, 45), #8b0000)';
      this.sidenavBackgroundImage = this.toolbarBackgroundImage;

      this.secondaryColor = '#ff0000';
      this.secondaryColor2 = '#ff0000';
    } else {
      this.logo = environment.COMPANY_LOGO_SRC;
      this.secondaryLogo = environment.SECONDARY_LOGO_SRC;

      this.secondaryColor = env.SECONDARY_COLOR || '#9ccafb';
      this.secondaryColor2 = env.SECONDARY_COLOR_2 || this.secondaryColor;

      this.toolbarBackgroundImage = `linear-gradient(to right, ${this.secondaryColor}, ${this.secondaryColor})`;
      this.sidenavBackgroundImage = `linear-gradient(to right, ${this.secondaryColor2}, ${this.secondaryColor2})`;
    }
  }

  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }

  loadSidenavConfig() {
    this.http
      .get<any[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe((data) => {
        this.menuItems = this.filterMenuItemsByAccess(data);
      });
  }

  toggleSidebar() {
    if (this.isMobileLayout) {
      this.isMobileDrawerOpen = !this.isMobileDrawerOpen;
      return;
    }
    this.isCollapsed = !this.isCollapsed;
    setTimeout(() => this.responsiveLayoutService.triggerReflow(), 350);
  }

  mouseenter() {
    if (!this.isExpanded) {
      this.isShowing = true;
    }
  }

  mouseleave() {
    if (!this.isExpanded) {
      this.isShowing = false;
    }
  }

  goToHelp() {
    const helpUrl = environment.HELP_URL || 'https://default.help.com';
    window.open(helpUrl, '_blank');
  }

  goToFeedback() {
    const feedbackUrl = environment.FEEDBACK_URL;
    if (feedbackUrl) window.open(feedbackUrl, '_blank');
  }

  showNotifications() {
    alert('Notifications clicked!');
  }

  openThemeSelector(): void {
    // Re-clicking the Theme Options button while the dialog is open used to
    // stack a second instance on top of the first. Guard against that by
    // tracking the live ref and bailing out if it's still open.
    if (this.themeSelectorDialogRef) {
      this.themeSelectorDialogRef.addPanelClass('dvm-theme-selector-pulse');
      return;
    }
    this.themeSelectorDialogRef = this.dialog.open(ThemeSelectorComponent, {
      panelClass: 'dvm-theme-selector-panel',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: false,
    });
    this.themeSelectorDialogRef.afterClosed().subscribe(() => {
      this.themeSelectorDialogRef = null;
    });
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  saveThemePreview(): void {
    this.themeService.commitPendingPreview();
    this.layoutService.commitPendingLayout();
  }

  slideshowPrev(): void {
    this.slideshowService.prev();
  }

  slideshowNext(): void {
    this.slideshowService.next();
  }

  toggleSlideshowPlayback(): void {
    if (this.slideshowPlaying) {
      this.slideshowService.pause();
    } else {
      this.slideshowService.play();
    }
  }

  slideshowApply(): void {
    this.slideshowService.applyAndStop();
  }

  slideshowSave(): void {
    this.slideshowService.saveAndStop();
  }

  slideshowStop(): void {
    this.slideshowService.cancelAndStop();
  }

  toggleSubmenu(menuName: string, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.expandedMenus.has(menuName)) {
      this.expandedMenus.delete(menuName);
    } else {
      this.expandedMenus.add(menuName);
    }
  }

  isMenuExpanded(menuName: string): boolean {
    return this.expandedMenus.has(menuName);
  }

  isActive(item: any): boolean {
    try {
      if (!item || !item.path) return false;
      return this.router.url.includes(item.path);
    } catch {
      return false;
    }
  }

  canShowMacroShortcut(): boolean {
    return true;
  }

  toggleMacroPanel(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.isMacroPanelOpen = !this.isMacroPanelOpen;
  }

  selectMacro(macro: string): void {
    this.isMacroPanelOpen = false;

    try {
      localStorage.setItem('selectedMacro', macro);
    } catch {}

    console.log('Macro selected:', macro);
  }

  hasChildren(item: any): boolean {
    return item.children && item.children.length > 0;
  }

  private filterMenuItemsByAccess(items: any[]): any[] {
    const allowedRoutes = this.getAllowedRoutesFromStorage();

    if (!allowedRoutes.size) {
      return [];
    }

    return (items || [])
      .map((item) => this.filterMenuItem(item, allowedRoutes))
      .filter((item): item is any => !!item);
  }

  private filterMenuItem(item: any, allowedRoutes: Set<string>): any | null {
    if (!item) {
      return null;
    }

    if (Array.isArray(item.children) && item.children.length > 0) {
      const children = item.children
        .map((child: any) => this.filterMenuItem(child, allowedRoutes))
        .filter((child: any) => !!child);

      if (!children.length) {
        return null;
      }

      return {
        ...item,
        children,
      };
    }

    if (!item.path) {
      return item;
    }

    return allowedRoutes.has(this.normalizeRoute(item.path)) ? item : null;
  }

  private getAllowedRoutesFromStorage(): Set<string> {
    try {
      const storedRoutes = localStorage.getItem('allowedRoutes');
      if (!storedRoutes) {
        return new Set<string>();
      }

      const parsedRoutes = JSON.parse(storedRoutes);
      if (!Array.isArray(parsedRoutes)) {
        return new Set<string>();
      }

      return new Set(
        parsedRoutes
          .filter((route) => typeof route === 'string')
          .map((route) => this.normalizeRoute(route))
      );
    } catch {
      return new Set<string>();
    }
  }

  private normalizeRoute(url: string): string {
    const cleanUrl = `${url || ''}`.split('?')[0].split('#')[0].trim().toLowerCase();
    const normalized = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;

    if (normalized === '/' || normalized === '') {
      return '/';
    }

    return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
  }

  private initToolbarState(): void {
    const env = environment as any;
    this.showThemeOptions = this.isFeatureEnabled(env.THEME_OPTIONS_ENABLED, true);
    this.showThemeToggle = this.isFeatureEnabled(env.THEME_TOGGLE_ENABLED, true);
    this.isDarkTheme = this.themeService.currentThemeName === 'dark';
  }

  private subscribeToSharedState(): void {
    this.subscriptions.add(
      this.dashboardTitleService.state$.subscribe((state) => {
        this.dashboardTitle = state.title;
        this.dashboardTitlePosition = state.position;
        this.dashboardTitleFontSize = state.fontSize;
      })
    );

    this.subscriptions.add(
      combineLatest([
        this.themeService.pendingPreview$,
        this.layoutService.pendingLayout$,
      ]).subscribe(([themePending, layoutPending]) => {
        this.hasPendingThemePreview = themePending || layoutPending;
      })
    );

    this.subscriptions.add(
      this.themeService.theme$.subscribe((theme) => {
        this.isDarkTheme =
          this.themeService.currentThemeName === 'dark' ||
          this.isDarkThemeByBackground(theme?.layout?.content?.bg);
      })
    );

    this.subscriptions.add(
      this.slideshowService.state$.subscribe((state) => {
        this.slideshowActive = state.active;
        this.slideshowPlaying = state.playing;
        this.slideshowIndex = state.index;
        this.slideshowTotal = state.items.length;
        this.slideshowCurrentName = state.current?.name || '';
      })
    );

    this.subscriptions.add(
      this.slideshowService.progress$.subscribe((progress) => {
        this.slideshowProgress = progress;
      })
    );
  }

  private getStoredSidebarWidth(): number {
    try {
      const stored = Number(localStorage.getItem(SideNavbarComponent.SIDEBAR_WIDTH_STORAGE_KEY));
      if (Number.isFinite(stored)) {
        return this.clampSidebarWidth(stored);
      }
    } catch {
      // Ignore storage errors and use the default width.
    }

    return SideNavbarComponent.SIDEBAR_DEFAULT_WIDTH;
  }

  private clampSidebarWidth(width: number): number {
    return Math.max(
      SideNavbarComponent.SIDEBAR_MIN_WIDTH,
      Math.min(SideNavbarComponent.SIDEBAR_MAX_WIDTH, Math.round(width))
    );
  }

  private isFeatureEnabled(value: unknown, defaultValue: boolean): boolean {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).toLowerCase() !== 'false';
  }

  private isDarkThemeByBackground(background: string | undefined): boolean {
    if (!background) {
      return false;
    }

    const hex = background.replace('#', '').slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(hex)) {
      return false;
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);
    return red * 0.299 + green * 0.587 + blue * 0.114 < 128;
  }
}
